import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';

const ThemeContext = createContext({
  theme: 'light',
  toggleTheme: () => {},
  setTheme: () => {},
  accent: 'orange',
  setAccent: () => {},
});

// HSL tuples per accent — hue+saturation stay the same across modes,
// lightness gets a small bump in dark so the color pops on navy.
const ACCENT_PRESETS = {
  orange: { light: '24 95% 53%', dark: '24 95% 60%', soft: { light: '24 95% 95%', dark: '24 95% 15%' } },
  blue:   { light: '217 91% 55%', dark: '217 91% 65%', soft: { light: '217 91% 95%', dark: '217 91% 15%' } },
  violet: { light: '262 83% 58%', dark: '262 83% 68%', soft: { light: '262 83% 95%', dark: '262 83% 15%' } },
  green:  { light: '142 71% 45%', dark: '142 71% 55%', soft: { light: '142 71% 95%', dark: '142 71% 15%' } },
  pink:   { light: '340 82% 52%', dark: '340 82% 62%', soft: { light: '340 82% 95%', dark: '340 82% 15%' } },
};
const ACCENT_KEYS = Object.keys(ACCENT_PRESETS);

// Reads the initial theme once, so React doesn't flash the wrong color.
// Priority: localStorage → system preference → light.
const getInitialTheme = () => {
  if (typeof window === 'undefined') return 'light';
  try {
    const stored = window.localStorage.getItem('vida:theme');
    if (stored === 'dark' || stored === 'light') return stored;
  } catch {}
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
};

const getInitialAccent = () => {
  if (typeof window === 'undefined') return 'orange';
  try {
    const stored = window.localStorage.getItem('vida:accent');
    if (ACCENT_KEYS.includes(stored)) return stored;
  } catch {}
  return 'orange';
};

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(getInitialTheme);
  const [accent, setAccentState] = useState(getInitialAccent);

  const applyTheme = useCallback((next) => {
    const root = document.documentElement;
    root.classList.toggle('dark', next === 'dark');
    // For QuickChart bg / iframe embeds that read colour-scheme.
    root.style.colorScheme = next;
  }, []);

  // Push the chosen accent into CSS variables so every `bg-primary`,
  // `text-primary`, `ring-primary` etc. reacts immediately.
  const applyAccent = useCallback((accentKey, themeKey) => {
    const preset = ACCENT_PRESETS[accentKey] || ACCENT_PRESETS.orange;
    const root = document.documentElement;
    const mode = themeKey === 'dark' ? 'dark' : 'light';
    root.style.setProperty('--primary', preset[mode]);
    root.style.setProperty('--primary-soft', preset.soft[mode]);
    root.style.setProperty('--ring', preset[mode]);
    // Primary foreground stays high-contrast — white in light, near-black in dark.
    root.style.setProperty('--primary-foreground', mode === 'dark' ? '222 47% 6%' : '0 0% 100%');
  }, []);

  useEffect(() => {
    applyTheme(theme);
    applyAccent(accent, theme);
    try {
      window.localStorage.setItem('vida:theme', theme);
      window.localStorage.setItem('vida:accent', accent);
    } catch {}
  }, [theme, accent, applyTheme, applyAccent]);

  const setTheme = useCallback((next) => setThemeState(next === 'dark' ? 'dark' : 'light'), []);
  const toggleTheme = useCallback(() => setThemeState((t) => (t === 'dark' ? 'light' : 'dark')), []);
  const setAccent = useCallback((next) => setAccentState(ACCENT_KEYS.includes(next) ? next : 'orange'), []);

  const value = useMemo(
    () => ({ theme, setTheme, toggleTheme, accent, setAccent }),
    [theme, setTheme, toggleTheme, accent, setAccent],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
export { ACCENT_KEYS, ACCENT_PRESETS };
